import { useState, useCallback } from "react";
import {
  useInviteCustomer,
  type InviteCustomerRequest,
} from "@/requests/customers/invite-customer";
import type { InviteEntryData } from "./InviteEntry";

export type EntryStatus = "pending" | "sending" | "success" | "error";

export interface SubmissionResult {
  successful: Array<{ entry: InviteEntryData; response: any }>;
  failed: Array<{ entry: InviteEntryData; error: string }>;
}

export function useInviteSubmission() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [entryStatuses, setEntryStatuses] = useState<
    Record<number, EntryStatus>
  >({});
  const [entryErrorMessages, setEntryErrorMessages] = useState<
    Record<number, string>
  >({});
  const inviteCustomerMutation = useInviteCustomer();

  const submitInvites = useCallback(
    async (
      validEntries: InviteEntryData[],
      entryIndexMap: Map<InviteEntryData, number>,
    ): Promise<SubmissionResult> => {
      setIsSubmitting(true);

      // Initialize all entries as pending
      const initialStatuses: Record<number, EntryStatus> = {};
      const initialErrors: Record<number, string> = {};
      validEntries.forEach((entry) => {
        const index = entryIndexMap.get(entry);
        if (index !== undefined) {
          initialStatuses[index] = "pending";
        }
      });
      setEntryStatuses(initialStatuses);
      setEntryErrorMessages(initialErrors);

      const result: SubmissionResult = {
        successful: [],
        failed: [],
      };

      // Submit each entry sequentially
      for (const entry of validEntries) {
        const entryIndex = entryIndexMap.get(entry);
        if (entryIndex === undefined) continue;

        // Mark as sending
        setEntryStatuses((prev) => ({ ...prev, [entryIndex]: "sending" }));

        try {
          // Generate customerId from email if not provided (for team member invites)
          const customerId = entry.customerId.trim() || "N/A";

          const requestData: InviteCustomerRequest = {
            firstName: entry.firstName,
            lastName: entry.lastName,
            email: entry.email,
            customerId: customerId,
            phone: entry.phone || undefined,
            customerHistoryFile: entry.customerHistoryFile || undefined,
          };

          const response =
            await inviteCustomerMutation.mutateAsync(requestData);

          // Mark as success
          setEntryStatuses((prev) => ({ ...prev, [entryIndex]: "success" }));
          result.successful.push({ entry, response });
        } catch (error: any) {
          const errorMessage =
            error?.error || error?.message || "Failed to send invitation";

          // Mark as error
          setEntryStatuses((prev) => ({ ...prev, [entryIndex]: "error" }));
          setEntryErrorMessages((prev) => ({
            ...prev,
            [entryIndex]: errorMessage,
          }));
          result.failed.push({ entry, error: errorMessage });
        }
      }

      setIsSubmitting(false);
      return result;
    },
    [inviteCustomerMutation],
  );

  const resetStatuses = useCallback(() => {
    setEntryStatuses({});
    setEntryErrorMessages({});
  }, []);

  return {
    isSubmitting,
    entryStatuses,
    entryErrorMessages,
    submitInvites,
    resetStatuses,
  };
}
